import React, { useMemo } from "react";
import {
  AdjustmentsHorizontalIcon,
  CpuChipIcon,
  CircleStackIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import NumberOfGPUsFilter from "./Modal/NumberOfGPUsFilter";
import MinimumCPUsFilter from "./Modal/MinimumCPUsFilter";
import MinimumRamFilter from "./Modal/MinimumRamFilter";
import PriceRangeFilter from "./Modal/PriceRangeFilter";
import FilterCard, { FilterRemoveButton } from "./FilterCard";

interface MoreFiltersPopoverProps {
  selectedGPUCount: string;
  setSelectedGPUCount: (gpuNum: string) => void;
  selectedMinCPUCount: number;
  setSelectedMinCPUCount: (number: number) => void;
  selectMinRam: string;
  setSelectMinRam: (ram: string) => void;
  priceRange: number[];
  setPriceRange: (number: number[]) => void;
}

interface FilterChip {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClear: () => void;
}

const MoreFiltersPopover: React.FC<MoreFiltersPopoverProps> = ({
  selectedGPUCount,
  setSelectedGPUCount,
  selectedMinCPUCount,
  setSelectedMinCPUCount,
  selectMinRam,
  setSelectMinRam,
  priceRange,
  setPriceRange,
}) => {
  const resetFilters = () => {
    setSelectedGPUCount("any");
    setSelectedMinCPUCount(0);
    setSelectMinRam("any");
    setPriceRange([0, 100]);
  };

  // Calculate active filters
  const activeFilters = useMemo(() => {
    const filters: FilterChip[] = [];
    if (selectedGPUCount !== "any") {
      filters.push({
        id: "gpu-count",
        label:
          selectedGPUCount === "8+"
            ? "8+ GPUs"
            : `${selectedGPUCount} GPU${selectedGPUCount === "1" ? "" : "s"}`,
        icon: <CpuChipIcon className="w-4 h-4" />,
        onClear: () => setSelectedGPUCount("any"),
      });
    }
    if (selectedMinCPUCount > 0) {
      filters.push({
        id: "cpu-count",
        label: `${selectedMinCPUCount}+ vCPUs`,
        icon: <CpuChipIcon className="w-4 h-4" />,
        onClear: () => setSelectedMinCPUCount(0),
      });
    }
    if (selectMinRam !== "any") {
      filters.push({
        id: "ram",
        label: `${selectMinRam}+ RAM`,
        icon: <CircleStackIcon className="w-4 h-4" />,
        onClear: () => setSelectMinRam("any"),
      });
    }
    if (priceRange[0] !== 0 || priceRange[1] !== 100) {
      filters.push({
        id: "price",
        label: `$${priceRange[0]}-$${priceRange[1]}/hr`,
        icon: <CurrencyDollarIcon className="w-4 h-4" />,
        onClear: () => setPriceRange([0, 100]),
      });
    }
    return filters;
  }, [
    selectedGPUCount,
    selectedMinCPUCount,
    selectMinRam,
    priceRange,
    setSelectedGPUCount,
    setSelectedMinCPUCount,
    setSelectMinRam,
    setPriceRange,
  ]);

  const filterSummary = useMemo(() => {
    if (activeFilters.length === 0) return undefined;
    return activeFilters.map((f) => f.label).join(", ");
  }, [activeFilters]);

  return (
    <Popover>
      <div className="relative inline-flex">
        <PopoverTrigger asChild>
          <FilterCard
            icon={<AdjustmentsHorizontalIcon className="w-4 h-4" />}
            title="More Filters"
            description={filterSummary}
            filterApplied={activeFilters.length > 0}
            hasTrailingAction={activeFilters.length > 0}
          />
        </PopoverTrigger>
        {activeFilters.length > 0 && (
          <FilterRemoveButton filterName="additional" onRemove={resetFilters} />
        )}
      </div>
      <PopoverContent
        className="w-[calc(100vw-2rem)] max-w-[600px] overflow-y-auto p-4 pb-8"
        side="bottom"
        align="end"
      >
        <div className="flex flex-col">
          <div className="flex flex-row justify-between items-center mb-4">
            <h1 className="text-white font-bold text-lg">Filters</h1>
            {activeFilters.length > 0 && (
              <button
                onClick={resetFilters}
                className="text-sm text-highlight hover:text-highlightDarker font-medium"
              >
                Clear All
              </button>
            )}
          </div>

          <hr className="w-full mb-4 dark:border dark:border-zinc-800" />

          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <h2 className="text-md text-gray-900 dark:text-white font-semibold mb-3">
                Compute Attributes
              </h2>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h3 className="text-sm text-gray-900 dark:text-white">
                      Number of GPUs
                    </h3>
                  </div>
                  <NumberOfGPUsFilter
                    selectedNumberOfGPUs={selectedGPUCount}
                    setSelectedNumberOfGPUs={setSelectedGPUCount}
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h3 className="text-sm text-gray-900 dark:text-white">
                      Minimum Ram{selectMinRam === "any" ? "" : ":"}
                    </h3>
                    {selectMinRam !== "any" && (
                      <h3 className="text-sm text-highlight ml-1">
                        at least {selectMinRam}
                      </h3>
                    )}
                  </div>
                  <MinimumRamFilter
                    selectedMinimumRam={selectMinRam}
                    setSelectedMinimumRam={setSelectMinRam}
                  />
                </div>

                <div className="flex flex-col">
                  <div className="flex flex-row items-center mb-3">
                    <h3 className="text-sm text-gray-900 dark:text-white">
                      Minimum CPUs{selectedMinCPUCount === 0 ? "" : ":"}
                    </h3>
                    {selectedMinCPUCount !== 0 && (
                      <h3 className="text-sm text-highlight ml-1">
                        {selectedMinCPUCount}
                      </h3>
                    )}
                  </div>
                  <MinimumCPUsFilter
                    selectedMinimumNumberOfCPUs={selectedMinCPUCount}
                    setSelectedMinimumNumberOfCPUs={setSelectedMinCPUCount}
                  />
                </div>
              </div>
            </div>

            <hr className="w-full dark:border dark:border-zinc-800" />
            <div className="flex flex-col">
              <div className="flex flex-row items-center mb-3">
                <h2 className="text-md text-gray-900 dark:text-white font-semibold">
                  Hourly Price Range
                </h2>
                <h3 className="text-sm text-highlight ml-1 mt-[1px]">
                  ${priceRange[0]} - ${priceRange[1]}
                </h3>
              </div>
              <PriceRangeFilter
                priceRange={priceRange}
                setPriceRange={setPriceRange}
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MoreFiltersPopover;
